/**
 * Tema de paleta (casca + destaque + cartões de resumo), incluindo o tema
 * PERSONALIZADO que o usuário monta com as próprias cores.
 *
 * Os temas prontos são blocos `[data-theme="<id>"]` em `tokens.css`. O
 * personalizado também tem bloco lá (`[data-theme="custom"]`), mas ele só
 * lê variáveis `--custom-*` que este módulo põe no `style` do `<html>`:
 * as cinco cores escolhidas e o que é DERIVADO delas (letra legível sobre
 * cada cor, destaque escurecido para virar texto, filtro do logo). Só
 * `--custom-*` vai inline — os tokens de verdade continuam no CSS, para o
 * modo escuro (`:root[data-mode="dark"]`) seguir vencendo onde precisa.
 *
 * Tudo mora no `localStorage`, como o modo de cor; nada passa pela API.
 * O `index.html` reaplica antes da primeira pintura lendo `vars` já
 * calculadas do JSON guardado — mexeu no formato aqui, mexe lá.
 */

import {
  contrastRatio,
  ensureContrast,
  hexToHsl,
  hslToHex,
  isLight,
  normalizeHex,
  readableTextOn
} from "@/shared/utils/color";

export const THEME_STORAGE_KEY = "reputation_theme";
export const CUSTOM_THEME_STORAGE_KEY = "reputation_custom_theme";
export const CUSTOM_THEME_ID = "custom";
export const DEFAULT_THEME_ID = "default";

/** O tema VAMOO (2026-10-15): o `default` com uma imagem de fundo na casca
 *  inteira, atrás do menu e da topbar (`--rail-image` em `tokens.css`,
 *  pintada pelo `.app-shell` em `shell.css`) e a logo trocada por
 *  "VAMOO" (`shell.css`). É tema OCULTO: o cartão só aparece no seletor pra
 *  quem digitou o código de desbloqueio no campo "Menu e moldura" do editor
 *  de tema (`#fall` — `l` não é dígito hex, então nunca colide com cor de
 *  verdade). A marca mora no `localStorage`, como o resto do tema, e não é
 *  controle de acesso: quem gravar a chave à mão fica com o tema. */
export const VAMOO_THEME_ID = "vamoo";
export const VAMOO_UNLOCK_STORAGE_KEY = "reputation_vamoo_unlocked";
/** Atributo no `<html>` que liga o universo e a logo (`tokens.css`,
 *  `shell.css`): vale pelo cartão VAMOO pronto E pelo tema personalizado
 *  com o universo ligado — um seletor só pros dois caminhos. */
export const VAMOO_ATTRIBUTE = "data-vamoo";
/** O que o campo do menu mostra enquanto o universo está ligado. */
export const VAMOO_UNLOCK_CODE = "#fall";

/** O que liga o VAMOO quando escrito no campo de cor do menu. */
export function isVamooUnlockCode(value: string): boolean {
  const code = value.trim().toLowerCase();
  return code === "#fall" || code === "fall";
}

/** Quem já destravou (ou já está com o universo ligado) vê o cartão. */
export function isVamooUnlocked(): boolean {
  return (
    storage()?.getItem(VAMOO_UNLOCK_STORAGE_KEY) === "1" ||
    readThemeId() === VAMOO_THEME_ID ||
    readCustomVamoo()
  );
}

/** Só a marca de desbloqueio, sem mexer no tema em uso. */
export function markVamooUnlocked(): void {
  storage()?.setItem(VAMOO_UNLOCK_STORAGE_KEY, "1");
}

/** Grava a marca e já aplica o tema pronto. */
export function unlockVamoo(): void {
  markVamooUnlocked();
  saveTheme(VAMOO_THEME_ID);
}

/** O tema personalizado guardado está com o universo ligado? */
export function readCustomVamoo(): boolean {
  const raw = storage()?.getItem(CUSTOM_THEME_STORAGE_KEY);
  if (!raw) return false;
  try {
    return (JSON.parse(raw) as { vamoo?: unknown }).vamoo === true;
  } catch {
    return false;
  }
}

function setVamooAttribute(root: HTMLElement, on: boolean): void {
  if (on) root.setAttribute(VAMOO_ATTRIBUTE, "");
  else root.removeAttribute(VAMOO_ATTRIBUTE);
}

export interface ThemeColors {
  /** Menu lateral, topbar e a moldura em volta do conteúdo. */
  rail: string;
  /** Botões, item ativo do menu, links, seleção. */
  accent: string;
  /** Os três cartões de resumo da Home e do dashboard. */
  card1: string;
  card2: string;
  card3: string;
}

export const THEME_COLOR_KEYS: readonly (keyof ThemeColors)[] = ["rail", "accent", "card1", "card2", "card3"];

/** Igual ao `[data-theme="default"]` — ponto de partida sem nada salvo. */
export const DEFAULT_THEME_COLORS: ThemeColors = {
  rail: "#1a1a1e",
  accent: "#305cff",
  card1: "#b8fa4e",
  card2: "#305cff",
  card3: "#94a6ff"
};

/** As cores do cartão VAMOO (2026-10-16, as da dona): menu preto sob a
 *  imagem, destaque violeta e os três cartões em degradê de roxo. O bloco
 *  `[data-theme="vamoo"]` de `tokens.css` espelha estes valores — o
 *  cartão do seletor pinta a miniatura daqui, a tela pinta de lá. */
export const VAMOO_THEME_COLORS: ThemeColors = {
  rail: "#000000",
  accent: "#834dfb",
  card1: "#c800ff",
  card2: "#5903b0",
  card3: "#25008a"
};

// ── leitura/gravação ──────────────────────────────────────────────────

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readThemeId(): string | null {
  return storage()?.getItem(THEME_STORAGE_KEY) ?? null;
}

/** As cores guardadas, validadas; `null` se não há tema personalizado (ou o JSON estragou). */
export function readCustomTheme(): ThemeColors | null {
  const raw = storage()?.getItem(CUSTOM_THEME_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { colors?: Partial<Record<keyof ThemeColors, unknown>> };
    const colors = {} as ThemeColors;
    for (const key of THEME_COLOR_KEYS) {
      const value = parsed.colors?.[key];
      const hex = typeof value === "string" ? normalizeHex(value) : null;
      if (!hex) return null;
      colors[key] = hex;
    }
    return colors;
  } catch {
    return null;
  }
}

/** `vamoo` é o universo ligado por `#fall` no editor: viaja no JSON ao
 *  lado das cores (o `index.html` o lê antes da primeira pintura) e
 *  também grava a marca de desbloqueio, pra o cartão pronto aparecer. */
export function saveCustomTheme(colors: ThemeColors, vamoo = false): void {
  const payload = JSON.stringify({ colors, vamoo, vars: deriveCustomThemeVars(colors) });
  storage()?.setItem(CUSTOM_THEME_STORAGE_KEY, payload);
  storage()?.setItem(THEME_STORAGE_KEY, CUSTOM_THEME_ID);
  if (vamoo) markVamooUnlocked();
  applyTheme(CUSTOM_THEME_ID);
}

export function saveTheme(themeId: string): void {
  storage()?.setItem(THEME_STORAGE_KEY, themeId);
  applyTheme(themeId);
}

// ── aplicação no <html> ───────────────────────────────────────────────

/** Tudo que o bloco `[data-theme="custom"]` de `tokens.css` lê. */
export function deriveCustomThemeVars(colors: ThemeColors): Record<string, string> {
  const railText = readableTextOn(colors.rail);
  return {
    "--custom-rail": colors.rail,
    // Mesmo off-white da casca padrão, em vez de branco puro.
    "--custom-rail-text": railText === "#ffffff" ? "#eef0f3" : railText,
    "--custom-logo-filter": isLight(colors.rail) ? "brightness(0)" : "brightness(0) invert(1)",
    "--custom-accent": colors.accent,
    "--custom-on-accent": readableTextOn(colors.accent),
    // O degradê do cabeçalho da Visão geral escurece as pontas quando o
    // texto sobre o destaque é claro, e CLAREIA quando é escuro (destaque
    // claro), senão o texto escuro some (2026-10-16).
    "--custom-hero-shade": readableTextOn(colors.accent) === "#ffffff" ? "#0b0d1a" : "#ffffff",
    "--custom-hero-third": readableTextOn(colors.accent) === "#ffffff" ? colors.card3 : colors.card1,
    // Destaque como LETRA sobre o conteúdo claro: escurece só se precisar.
    "--custom-accent-ink": ensureContrast(colors.accent, "#ffffff"),
    "--custom-card-1": colors.card1,
    "--custom-card-1-text": readableTextOn(colors.card1),
    "--custom-card-2": colors.card2,
    "--custom-card-2-text": readableTextOn(colors.card2),
    "--custom-card-3": colors.card3,
    "--custom-card-3-text": readableTextOn(colors.card3)
  };
}

function clearCustomVars(root: HTMLElement): void {
  for (const name of Object.keys(deriveCustomThemeVars(DEFAULT_THEME_COLORS))) {
    root.style.removeProperty(name);
  }
}

function setCustomVars(root: HTMLElement, colors: ThemeColors): void {
  for (const [name, value] of Object.entries(deriveCustomThemeVars(colors))) {
    root.style.setProperty(name, value);
  }
}

/** Aplica um tema pronto ou o personalizado guardado. Personalizado sem
 *  cores guardadas (storage limpo pela metade) cai no padrão. */
export function applyTheme(themeId: string): void {
  const root = document.documentElement;
  clearCustomVars(root);
  let vamoo = themeId === VAMOO_THEME_ID;
  if (themeId === CUSTOM_THEME_ID) {
    const colors = readCustomTheme();
    if (!colors) {
      root.setAttribute("data-theme", DEFAULT_THEME_ID);
      setVamooAttribute(root, false);
      return;
    }
    setCustomVars(root, colors);
    vamoo = readCustomVamoo();
  }
  root.setAttribute("data-theme", themeId);
  setVamooAttribute(root, vamoo);
}

/** Pinta o app com cores ainda NÃO salvas (pré-visualização do editor). */
export function previewCustomTheme(colors: ThemeColors, vamoo = false): void {
  const root = document.documentElement;
  setCustomVars(root, colors);
  root.setAttribute("data-theme", CUSTOM_THEME_ID);
  setVamooAttribute(root, vamoo);
}

// ── avisos ────────────────────────────────────────────────────────────

export interface ThemeWarning {
  field: keyof ThemeColors;
  message: string;
}

const FIELD_NAMES: Record<keyof ThemeColors, string> = {
  rail: "O menu",
  accent: "O destaque",
  card1: "O cartão 1",
  card2: "O cartão 2",
  card3: "O cartão 3"
};

/** Verde e vermelho saturados são a linguagem de POLARIDADE (Positivo,
 *  Negativo); o pêssego claro é o Desfavorável. Os temas prontos evitam
 *  essas famílias — o personalizado pode usar, mas fica avisado. */
function polarityLookalike(hex: string): string | null {
  const [h, s, l] = hexToHsl(hex);
  if (s < 0.3 || l < 0.12 || l > 0.92) return null;
  // A partir de 88°: o verde-limão do tema padrão (~83°) fica de fora;
  // Positivo e Favorável da marca estão em ~96°.
  if (h >= 88 && h <= 160) return "Positivo/Favorável (verde)";
  if ((h >= 345 || h <= 12) && l < 0.7) return "Negativo (vermelho)";
  if (h > 12 && h <= 35 && l >= 0.75) return "Desfavorável (pêssego)";
  return null;
}

export function assessThemeColors(colors: ThemeColors): ThemeWarning[] {
  const warnings: ThemeWarning[] = [];

  if (contrastRatio(colors.accent, colors.rail) < 1.6) {
    warnings.push({
      field: "accent",
      message: "O destaque quase some sobre o menu: o item ativo fica difícil de distinguir."
    });
  }

  for (const field of ["accent", "card1", "card2", "card3"] as const) {
    const lookalike = polarityLookalike(colors[field]);
    if (lookalike) {
      warnings.push({
        field,
        message: `${FIELD_NAMES[field]} lembra a cor de ${lookalike} — em telas com sentimento pode ser confundido com polaridade.`
      });
    }
  }

  const cards = [colors.card1, colors.card2, colors.card3];
  if (new Set(cards).size < cards.length) {
    warnings.push({ field: "card1", message: "Há cartões com a mesma cor: os resumos lado a lado ficam indistinguíveis." });
  }

  return warnings;
}

/** O destaque só vira letra depois de escurecido? Dito na tela, para o
 *  usuário não estranhar o link num tom diferente do botão. */
export function accentNeedsDarkerText(accent: string): boolean {
  return ensureContrast(accent, "#ffffff") !== accent;
}

// ── gerador ───────────────────────────────────────────────────────────

/** Gira o matiz para fora das famílias de polaridade. */
function awayFromPolarity(h: number): number {
  const hue = ((h % 360) + 360) % 360;
  if (hue >= 88 && hue <= 160) return 190;
  if (hue >= 345 || hue <= 12) return 275;
  return hue;
}

/**
 * Uma combinação inteira a partir de UMA cor: menu num tom bem escuro do
 * mesmo matiz, cartão 1 = o destaque, cartão 2 = um tom claro dele e
 * cartão 3 = o complementar, desviado de verde e vermelho.
 */
export function generateFromAccent(accentInput: string): ThemeColors {
  const accent = normalizeHex(accentInput) ?? DEFAULT_THEME_COLORS.accent;
  const [h, s] = hexToHsl(accent);
  return {
    rail: hslToHex(h, Math.min(Math.max(s * 0.45, 0.12), 0.4), 0.12),
    accent,
    card1: accent,
    card2: hslToHex(h, Math.min(s, 0.75), 0.86),
    card3: hslToHex(awayFromPolarity(h + 180), 0.7, 0.62)
  };
}
